import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import useUserContext from 'shared/users/contexts/useUserContext'
import { SessionState, TERMINAL_PHASES } from '../state/types'
import {
  attachedFileNames, clockTime, resolveSegmentTraces, splitTimeline, threadSegments,
} from '../util/activity'
import { ActivityLog } from './ActivityLog'
import { AnswerCard } from './AnswerCard'
import { Composer } from './Composer'
import { ConfirmationCard } from './ConfirmationCard'
import { OperatorTurn } from './OperatorTurn'
import { QuestionPrompt } from './QuestionPrompt'
import { RunningStep } from './RunningStep'

interface SessionBodyProps { session: SessionState }

export function SessionBody({ session }: Readonly<SessionBodyProps>) {
  const { t } = useTranslation()
  const { isAdmin } = useUserContext()
  const terminal = TERMINAL_PHASES.includes(session.phase)
  const attached = attachedFileNames(session.timeline)
  // Segment the WHOLE timeline: every turn gets its own work log and its own answer card, rather
  // than one answer card for the newest turn and every earlier answer buried inside a log headed
  // "Worked for N s".
  const segments = threadSegments(session.timeline)
  const traces = resolveSegmentTraces(segments)
  const firstStamped = session.timeline.find((i) => i.at > 0)?.at ?? 0
  // Agent work only ever appends, so live work — if any — is always the FINAL segment. It is not
  // enough to take the last `work` segment: the operator's next message lands as a `turn` segment
  // before the agent emits anything for it, and the newest work segment is then a finished one
  // whose answer must stay in its own card, not be pulled back into the log behind "Thinking…".
  const lastIndex = segments.length - 1
  const liveWorkIndex = segments[lastIndex]?.kind === 'work' ? lastIndex : -1

  return (
    <div className="copilot-session">
      <div className="copilot-transcript">
        {firstStamped > 0 && (
          <div className="copilot-day" data-testid="copilot-day-divider">
            <span>{`${new Date(firstStamped).toLocaleDateString()} ${clockTime(firstStamped)}`}</span>
          </div>
        )}
        {segments.map((seg, i) => {
          if (seg.kind === 'turn') return <OperatorTurn key={seg.item.id} item={seg.item} />
          // The live segment keeps everything in its log so the operator watches work happen; a
          // finished one hands its trailing prose to an answer card. Splitting the live segment too
          // would make progress chatter flicker in and out of the card as each tool call lands.
          const live = !terminal && i === liveWorkIndex
          const { activity: steps, answer } = splitTimeline(seg.items, !live)
          return (
            // eslint-disable-next-line react/no-array-index-key -- a work run has no id of its own
            <Fragment key={`work-${i}`}>
              {isAdmin && steps.length > 0 && (
                <ActivityLog items={steps} attached={attached} expanded={live} running={live} trace={traces[i]} />
              )}
              <AnswerCard items={answer} />
            </Fragment>
          )
        })}
        {session.phase === 'queued' && <p className="copilot-waiting">{t('copilot.waiting')}</p>}
        <RunningStep session={session} />
        {session.pendingConfirmation && <ConfirmationCard session={session} />}
        {session.pendingQuestion && <QuestionPrompt session={session} />}
      </div>
      <Composer session={session} />
    </div>
  )
}
